/**
 * VersionBadge Component (V2)
 *
 * Zeigt Status-Badge für Versionen (draft, published, scheduled, etc.)
 * WCAG 2.1 Level AA + BFSG konform
 */

import {
  CheckCircle,
  Clock,
  RotateCcw,
  History,
  Pencil,
  Globe,
  Archive,
  Circle,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import { Badge } from '@/components/v2/ui'
import type { VersionStatus, SourceType } from '@/types/versioning'

interface VersionBadgeProps {
  status: VersionStatus
  sourceType?: SourceType
  isCurrent?: boolean
  isScheduled?: boolean
  className?: string
}

export function VersionBadge({
  status,
  sourceType,
  isCurrent,
  isScheduled,
  className = '',
}: VersionBadgeProps) {
  const { t } = useTranslation()
  // Priority: Current > Scheduled > Status
  if (isCurrent) {
    return (
      <Badge variant="success" className={className}>
        <CheckCircle className="h-3 w-3 mr-1" />
        Aktuell
      </Badge>
    )
  }

  if (isScheduled) {
    return (
      <Badge variant="warning" className={className}>
        <Clock className="h-3 w-3 mr-1" />
        {t('common.scheduled')}
      </Badge>
    )
  }

  // Source type badges for special cases
  if (sourceType === 'rollback') {
    return (
      <Badge variant="info" className={className}>
        <RotateCcw className="h-3 w-3 mr-1" />
        Wiederhergestellt
      </Badge>
    )
  }

  if (sourceType === 'scheduled_restore') {
    return (
      <Badge variant="secondary" className={className}>
        <History className="h-3 w-3 mr-1" />
        Auto-Restore
      </Badge>
    )
  }

  // Regular status badges
  switch (status) {
    case 'draft':
      return (
        <Badge variant="secondary" className={className}>
          <Pencil className="h-3 w-3 mr-1" />
          {t('common.draft')}
        </Badge>
      )
    case 'published':
      return (
        <Badge variant="primary" className={className}>
          <Globe className="h-3 w-3 mr-1" />
          {t('publishing.published')}
        </Badge>
      )
    case 'archived':
      return (
        <Badge variant="secondary" className={cn('bg-neutral-700 text-neutral-200', className)}>
          <Archive className="h-3 w-3 mr-1" />
          Archiviert
        </Badge>
      )
    default:
      return null
  }
}

/**
 * Compact version badge (icon only)
 */
export function VersionBadgeCompact({
  status,
  isCurrent,
  isScheduled,
  className = '',
}: Omit<VersionBadgeProps, 'sourceType'>) {
  const { t } = useTranslation()
  if (isCurrent) {
    return (
      <span
        className={cn('text-green-500', className)}
        title="Aktuell"
        aria-label="Aktuell"
      >
        <CheckCircle className="h-4 w-4 fill-current" />
      </span>
    )
  }

  if (isScheduled) {
    return (
      <span
        className={cn('text-amber-500', className)}
        title={t('common.scheduled')}
        aria-label={t('common.scheduled')}
      >
        <Clock className="h-4 w-4 fill-current" />
      </span>
    )
  }

  switch (status) {
    case 'draft':
      return (
        <span
          className={cn('text-neutral-400', className)}
          title={t('common.draft')}
          aria-label={t('common.draft')}
        >
          <Circle className="h-4 w-4" />
        </span>
      )
    case 'published':
      return (
        <span
          className={cn('text-primary-500', className)}
          title={t('publishing.publishedAria')}
          aria-label={t('publishing.publishedAria')}
        >
          <Circle className="h-4 w-4 fill-current" />
        </span>
      )
    case 'archived':
      return (
        <span
          className={cn('text-neutral-500', className)}
          title="Archiviert"
          aria-label="Archiviert"
        >
          <Archive className="h-4 w-4 fill-current" />
        </span>
      )
    default:
      return null
  }
}
